import { Files, Phone, Search, Tv2, UserRoundCog, type LucideIcon } from 'lucide-react';
import { defineMessages, type IntlShape } from 'react-intl';

import { weightedSample } from './components/work-home-ranking-policy';

/**
 * Static and PHI-free.
 *
 * `label` is the chip; `prompt` is what gets SUBMITTED when it is clicked, and
 * the two are separate fields so a copy edit to a chip cannot silently change
 * the instruction the agent receives. Most labels here already read as a whole
 * instruction, so they carry no `prompt` and are submitted as-is — the field is
 * present for the elliptical ones, which read as a heading rather than a request.
 */
const suggestionMessages = defineMessages({
  preChartLabel: {
    defaultMessage: 'Pre-chart for tomorrow',
    id: 'l0vtbl5yyC',
    description: 'Work home suggestion chip: pre-chart tomorrow’s appointments',
  },
  preChartPrompt: {
    defaultMessage: 'Pre-chart my appointments for tomorrow',
    id: '1QQHooW7RR',
    description: 'Work home suggestion submit prompt: pre-chart tomorrow’s appointments',
  },
  pushNotesLabel: {
    defaultMessage: 'Push notes to my EHR',
    id: 'X3U7Xvp3jz',
    description: 'Work home suggestion chip: push session notes to the EHR',
  },
  pushNotesPrompt: {
    defaultMessage: 'Push my session notes to my EHR',
    id: 'DxsiqdwXUz',
    description: 'Work home suggestion submit prompt: push session notes to the EHR',
  },
  lookUpTreatment: {
    defaultMessage: 'Help me look up a treatment plan',
    id: 'ibDmzjgFok',
    description: 'Work home suggestion chip: look up a treatment plan',
  },
  draftFollowUpSms: {
    defaultMessage: 'Draft a patient follow-up SMS',
    id: '170/IVrUVe',
    description: 'Work home suggestion chip: draft a patient follow-up SMS',
  },
  summariseAdminTasks: {
    defaultMessage: 'Summarise my open admin tasks',
    id: 'r9BWsZR1dW',
    description: 'Work home suggestion chip: summarise open admin tasks',
  },
  findDocuments: {
    defaultMessage: 'Find documents I still need to upload',
    id: '/qM50Jdmwm',
    description: 'Work home suggestion chip: find documents still needing upload',
  },
  prepareConsult: {
    defaultMessage: 'Prepare my next consult',
    id: 'MDCP4F/2n0',
    description: 'Work home suggestion chip: prepare the next consult',
  },
  createReferral: {
    defaultMessage: 'Create a referral letter',
    id: 'pa79+GF8uy',
    description: 'Work home suggestion chip: create a referral letter',
  },
  checkActionToday: {
    defaultMessage: 'Check what needs action today',
    id: 'i60xm3Iti0',
    description: 'Work home suggestion chip: check what needs action today',
  },
});

/** Shared with Suggested work so the drawer and the tray submit the same job. */
export const workHomeTrayPreChartLabel = suggestionMessages.preChartLabel;
export const workHomeTrayPreChartPrompt = suggestionMessages.preChartPrompt;
export const workHomeTrayPushNotesLabel = suggestionMessages.pushNotesLabel;
export const workHomeTrayPushNotesPrompt = suggestionMessages.pushNotesPrompt;

export type WorkHomeSuggestion = {
  id: string;
  label: string;
  prompt?: string;
  icon: LucideIcon;
};

export const WORK_HOME_TRAY_PRE_CHART_ID = 'tray-pre-chart';
export const WORK_HOME_TRAY_PUSH_NOTES_ID = 'tray-push-notes';
export const WORK_HOME_TRAY_LOOK_UP_TREATMENT_ID = 'tray-look-up-treatment';
export const WORK_HOME_TRAY_DRAFT_FOLLOW_UP_SMS_ID = 'tray-draft-follow-up-sms';
export const WORK_HOME_TRAY_SUMMARISE_ADMIN_TASKS_ID = 'tray-summarise-admin-tasks';
export const WORK_HOME_TRAY_FIND_DOCUMENTS_ID = 'tray-find-documents';
export const WORK_HOME_TRAY_PREPARE_CONSULT_ID = 'tray-prepare-consult';
export const WORK_HOME_TRAY_CREATE_REFERRAL_ID = 'tray-create-referral';
export const WORK_HOME_TRAY_CHECK_ACTION_TODAY_ID = 'tray-check-action-today';

/** How many chips the tray shows at once. */
export const WORK_HOME_TRAY_VISIBLE_COUNT = 3;

/**
 * Pre-chart and treatment lookup are the chips we want recommended more often
 * than the rest of the tray. Weight is relative to every other chip at 1.
 */
const WORK_HOME_TRAY_BOOST_WEIGHT = 3;

const WORK_HOME_TRAY_BOOSTED_IDS: ReadonlySet<string> = new Set([
  WORK_HOME_TRAY_PRE_CHART_ID,
  WORK_HOME_TRAY_LOOK_UP_TREATMENT_ID,
]);

const workHomeTrayWeight = (id: string): number =>
  WORK_HOME_TRAY_BOOSTED_IDS.has(id) ? WORK_HOME_TRAY_BOOST_WEIGHT : 1;

const MAX_TRAY_RESHUFFLE_ATTEMPTS = 8;

export const pickWorkHomeTrayIds = (seed: number): readonly string[] =>
  weightedSample(
    [
      WORK_HOME_TRAY_PRE_CHART_ID,
      WORK_HOME_TRAY_PUSH_NOTES_ID,
      WORK_HOME_TRAY_LOOK_UP_TREATMENT_ID,
      WORK_HOME_TRAY_DRAFT_FOLLOW_UP_SMS_ID,
      WORK_HOME_TRAY_SUMMARISE_ADMIN_TASKS_ID,
      WORK_HOME_TRAY_FIND_DOCUMENTS_ID,
      WORK_HOME_TRAY_PREPARE_CONSULT_ID,
      WORK_HOME_TRAY_CREATE_REFERRAL_ID,
      WORK_HOME_TRAY_CHECK_ACTION_TODAY_ID,
    ],
    WORK_HOME_TRAY_VISIBLE_COUNT,
    seed,
    workHomeTrayWeight,
    (id) => id
  );

/** Next seed whose chips are not the ones already on screen. */
export const nextWorkHomeTraySeed = (start: number, avoidKey: string): number => {
  let seed = start >>> 0;
  for (let attempt = 0; attempt < MAX_TRAY_RESHUFFLE_ATTEMPTS; attempt += 1) {
    if (pickWorkHomeTrayIds(seed).join('|') !== avoidKey) return seed;
    seed = (seed + 1) >>> 0;
  }
  return seed;
};

export const getWorkHomeSuggestionSets = (
  intl: IntlShape
): readonly (readonly WorkHomeSuggestion[])[] => [
  [
    {
      id: WORK_HOME_TRAY_PRE_CHART_ID,
      label: intl.formatMessage(suggestionMessages.preChartLabel),
      prompt: intl.formatMessage(suggestionMessages.preChartPrompt),
      icon: Tv2,
    },
    {
      id: WORK_HOME_TRAY_PUSH_NOTES_ID,
      label: intl.formatMessage(suggestionMessages.pushNotesLabel),
      prompt: intl.formatMessage(suggestionMessages.pushNotesPrompt),
      icon: Tv2,
    },
    {
      id: WORK_HOME_TRAY_LOOK_UP_TREATMENT_ID,
      label: intl.formatMessage(suggestionMessages.lookUpTreatment),
      icon: Search,
    },
  ],
  [
    {
      id: WORK_HOME_TRAY_DRAFT_FOLLOW_UP_SMS_ID,
      label: intl.formatMessage(suggestionMessages.draftFollowUpSms),
      icon: Phone,
    },
    {
      id: WORK_HOME_TRAY_SUMMARISE_ADMIN_TASKS_ID,
      label: intl.formatMessage(suggestionMessages.summariseAdminTasks),
      icon: UserRoundCog,
    },
    {
      id: WORK_HOME_TRAY_FIND_DOCUMENTS_ID,
      label: intl.formatMessage(suggestionMessages.findDocuments),
      icon: Files,
    },
  ],
  [
    {
      id: WORK_HOME_TRAY_PREPARE_CONSULT_ID,
      label: intl.formatMessage(suggestionMessages.prepareConsult),
      icon: Tv2,
    },
    {
      id: WORK_HOME_TRAY_CREATE_REFERRAL_ID,
      label: intl.formatMessage(suggestionMessages.createReferral),
      icon: Files,
    },
    {
      id: WORK_HOME_TRAY_CHECK_ACTION_TODAY_ID,
      label: intl.formatMessage(suggestionMessages.checkActionToday),
      icon: Search,
    },
  ],
];
